import { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';

interface BarcodeScannerProps {
  onScanSuccess: (decodedText: string) => void;
  onClose: () => void;
}

export function BarcodeScanner({ onScanSuccess, onClose }: BarcodeScannerProps) {
  const { t } = useI18n();
  const scannerRef = useRef<HTMLDivElement>(null);
  const html5QrCodeRef = useRef<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const stopScanner = useCallback(async () => {
    const scanner = html5QrCodeRef.current as { stop?: () => Promise<void>; clear?: () => Promise<void> } | null;
    if (scanner) {
      try {
        if (scanner.stop) await scanner.stop();
        if (scanner.clear) await scanner.clear();
      } catch {
        // Ignore cleanup errors
      }
      html5QrCodeRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function startScanner() {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');

        if (cancelled || !scannerRef.current) return;

        const scannerId = 'barcode-scanner-' + Date.now();
        scannerRef.current.id = scannerId;

        const html5QrCode = new Html5Qrcode(scannerId);
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 280, height: 150 },
            aspectRatio: 1.0,
          },
          (decodedText: string) => {
            if (navigator.vibrate) {
              navigator.vibrate(100);
            }
            onScanSuccess(decodedText);
          },
          () => {
            // QR code scan failure — ignore (continuous scanning)
          }
        );
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message || 'Failed to start camera');
        }
      }
    }

    startScanner();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [onScanSuccess, stopScanner]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center">
      <button
        onClick={() => {
          stopScanner();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 hover:bg-white/30
          text-white transition-colors cursor-pointer"
      >
        <X size={24} />
      </button>

      <div className="w-full max-w-md px-4">
        <p className="text-white text-center text-sm mb-4 opacity-80">
          {t('tyre.pointCamera')}
        </p>
        <div
          ref={scannerRef}
          className="rounded-lg overflow-hidden bg-black"
        />
        {error && (
          <div className="mt-4 p-3 rounded-md bg-red/20 border border-red/30 text-red text-sm text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export function isCameraAvailable(): boolean {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}
